import { NOTE_TARGET, StickyNotes } from '@components/ArtBoard/StickyNotes'
import { selectRootNodesBySection } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { selectCompactLayout } from '@store/reducers/artboard'
import {
  selectIsForceExpanded,
  selectTagVisibility,
  selectVisibleIds,
} from '@store/reducers/tags/visibility'
import type { AppState } from '@store/types'
import cx from 'classnames'
import type React from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import { useArtboardMode } from '@/hooks'
import { CompactBranch } from '../CompactLayout'
import TreeBranch from '../TreeBranch/TreeBranch'
import { EmptySectionDropzone } from './EmptySectionDropzone'
import { SectionRow } from './SectionRow/SectionRow'

type Props = {
  id: string
  index: number
  last?: boolean
}

// @TODO refactor all this component
export const Section: React.FC<Props> = ({ id, index, last = false }) => {
  const compact = useSelector(selectCompactLayout)
  const defaultColorId = useSelector(
    migrationProjectSelector.getProjectDefaultColorId
  )
  const defaultColor = useSelector(
    migrationProjectSelector.getProjectColorForUI(defaultColorId)
  )

  const rootNodeIds = useSelector(selectRootNodesBySection(id), shallowEqual)

  // Page-tag filter (OCT-5007): a section with no matching page inside is
  // hidden together with its header, and the connector-line flags below are
  // derived from the visible roots only.
  const visibleRootNodeIds = useSelector(
    selectVisibleIds(rootNodeIds),
    shallowEqual
  )

  const { filterIsActive } = useSelector(selectTagVisibility)

  const isForceExpanded = useSelector(selectIsForceExpanded(id))

  const isCollapsed = useSelector(
    migrationProjectSelector.getProjectSectionIsCollapsed(id)
  )

  // a collapsed section holding a match is expanded for the duration of the
  // filter only; the project's own flag is left alone
  const collapsed = isCollapsed && !isForceExpanded

  const active = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.section.selectedSectionId === id
  )

  const canEdit = useSelector(migrationProjectSelector.getProjectAccessCanEdit)

  const { seoMode } = useArtboardMode()

  if (filterIsActive && visibleRootNodeIds.length === 0) {
    return null
  }

  return (
    <div
      id={`section_${id}`}
      className={cx('Tree-footer', {
        collapsed,
        active,
        'Tree-footer__not-last': !last,
      })}
      // a collapsed section does not draw its notes, so a note dropped on it
      // would vanish: it goes back to the tab instead
      {...(!collapsed && { [NOTE_TARGET]: id })}
    >
      <SectionRow index={index} last={last} id={id} />
      {/* the notes go away with the pages while the section is collapsed */}
      {!collapsed && !seoMode && <StickyNotes parentId={id} anchor="section" />}
      <div
        className="Tree-children is-group is-row"
        // @ts-expect-error
        style={{ '--tree-color': defaultColor }}
      >
        {/* no page to drop next to — the section itself takes the page */}
        {rootNodeIds.length === 0 && !collapsed && canEdit && (
          <EmptySectionDropzone sectionId={id} />
        )}
        {visibleRootNodeIds.map((rootId, i) => {
          const isOne = visibleRootNodeIds.length === 1
          const isLast = !isOne && i === visibleRootNodeIds.length - 1
          const isFirst = !isOne && i === 0
          const isChild = !isOne && !isFirst && !isLast

          return (
            <CompactBranch key={rootId} enabled={compact}>
              <TreeBranch
                depth={0}
                key={rootId}
                nodeId={rootId}
                sectionId={id}
                // real position among all roots — the drop zones use it
                index={rootNodeIds.indexOf(rootId)}
                isFirst={isFirst}
                isOne={isOne}
                isLast={isLast}
                isChild={isChild}
                isFooter
              />
            </CompactBranch>
          )
        })}
      </div>
    </div>
  )
}
